"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports._createWebsocketConnection = void 0;
const backoff_1 = __importDefault(require("backoff"));
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
const shared_utils_iso_1 = require("@wranggle/shared-utils-iso");
const is_housekeeping_message_1 = require("./is-housekeeping-message");
const await_focus_browser_support_1 = require("./await-focus-browser-support");
const MinPingMs = 3499;
const DefaultOpts = {
    /**
     * This default does not add auth request headers! Override to do so.
     * A server-side client like 'ws' accepts some options like { headers } as a second param, and browsers accept multiple "protocol" strings.
     * @param url
     * @param opts
     */
    webSocketFactory: (url, opts) => new isomorphic_ws_1.default(url, opts),
    backoffOpts: {
        backoffInitialDelay: 500,
        backoffMaxDelayMs: 1000 * 60 * 5,
        // to do: max attempts
    },
    pingMs: 1000 * 22,
};
/**
 * Starts and maintains a websocket connection to the specified URL.
 * This is fairly low level, and generic to standard websocket servers.
 * It uses a fibonacci backoff scheme for reconnection attempts.
 * (note: borrowed this from webstack-micro, which prob borrowed from some other lib, forgot. Maybe there's a decent one that exposes
 *  listeners and that has a good backoff system. If so, can replace, since this one doesn't have tests.)
 * @param opts
 */
function _createWebsocketConnection(opts) {
    const { onConnectionAttempt, webSocketFactory } = Object.assign({}, DefaultOpts, opts);
    const pingMs = typeof opts.pingMs === 'number' ? Math.max(opts.pingMs, MinPingMs) : opts.pingMs ? DefaultOpts.pingMs : -1;
    let _connectionDesired = true;
    let _hasEverConnected = false;
    const logger = shared_utils_iso_1.loggerIsoLib();
    let _sanitizedUrlForLog;
    let fetchUrlFn;
    if (typeof opts.requestAuthenticatedWsUrl === 'function') {
        fetchUrlFn = opts.requestAuthenticatedWsUrl;
    }
    else if (typeof opts.url === 'string') {
        fetchUrlFn = () => Promise.resolve(opts.url);
    }
    else {
        throw new Error('ReconnectingWebsocket requires either "requestAuthenticatedWsUrl" async function or "url" string as field in constructor options param');
    }
    const isReconnectDesiredFn = opts.isReconnectDesired ? opts.isReconnectDesired : await_focus_browser_support_1.buildDefaultIsReconnectDesiredFn();
    const listenersByEvent = {};
    let webSocket;
    const addWebSocketListener = (type, fn) => {
        if (typeof fn !== 'function') {
            logger.error('addWebSocketListener expecting an event handler function');
            return;
        }
        listenersByEvent[type] = listenersByEvent[type] || [];
        listenersByEvent[type].push(fn);
    };
    const removeWebSocketListener = (type, fn) => {
        if (!listenersByEvent[type]) {
            return;
        }
        if (typeof fn !== 'function') {
            logger.error('Reference to specific listener required'); // because not currently distinguishing between lower-level ones set in realtime-context.jsx and view-specific listeners
            return;
        }
        if (webSocket) {
            webSocket.removeEventListener(type, fn);
        }
        const ndx = listenersByEvent[type].findIndex((existing) => existing === fn);
        if (ndx >= 0) {
            listenersByEvent[type].splice(ndx, 1);
        }
    };
    const getCurrentWebSocket = () => webSocket;
    const backoffOpts = Object.assign({}, DefaultOpts.backoffOpts, opts.backoffOpts);
    const fibonacciBackoff = backoff_1.default.fibonacci({
        randomisationFactor: 0,
        initialDelay: backoffOpts.backoffInitialDelay,
        maxDelay: backoffOpts.backoffMaxDelayMs,
    });
    const triggerEventListeners = (eventType, evt) => {
        (listenersByEvent[eventType] || []).forEach((fn) => {
            try {
                fn(evt);
            }
            catch (err) {
                logger.error({ error: shared_utils_iso_1.jsErrorToRecord(err) }, `Uncaught error in WebSocket "${eventType}" listener"`);
            }
        });
    };
    const onOpen = (evt) => {
        logger.info({ url: _sanitizedUrlForLog }, 'WebSocket connected');
        _hasEverConnected = true;
        fibonacciBackoff.reset();
        _markActivity();
        triggerEventListeners('open', evt);
    };
    const onClose = (evt) => {
        logger.info({ url: _sanitizedUrlForLog }, 'WebSocket closed');
        fibonacciBackoff.backoff();
        triggerEventListeners('close', evt);
    };
    let _activityTimer;
    const _markActivity = () => {
        clearTimeout(_activityTimer);
        if (pingMs <= 0) {
            return;
        }
        _activityTimer = setTimeout(() => {
            if (_connectionDesired) {
                const socket = getCurrentWebSocket();
                if (socket) {
                    try {
                        if (typeof socket.ping === 'function') { // seems to be missing on browser socket
                            socket.ping();
                        }
                        else {
                            socket.send(JSON.stringify({ ping: true, wantReply: false }));
                        }
                    }
                    catch (err) {
                        logger.warn({ error: shared_utils_iso_1.jsErrorToRecord(err) }, 'Failed to ping server');
                        // todo: perhaps check readyState and if it thinks it is open, close/reconnect
                    }
                }
            }
        }, pingMs);
    };
    const onMessage = (evt) => {
        let shouldTrigger = true;
        if (is_housekeeping_message_1.isHousekeepingMessage(evt.data)) {
            try {
                const data = JSON.parse(evt.data);
                if (data.clientShouldDisconnect) {
                    disconnect();
                }
                else if (data.pong) {
                    shouldTrigger = false;
                }
            }
            catch (err) {
                logger.error({ error: shared_utils_iso_1.jsErrorToRecord(err), data: evt.data }, 'Failed to parse housekeeping message');
            }
        }
        _markActivity();
        if (shouldTrigger) {
            triggerEventListeners('message', evt);
        }
    };
    const onError = (evt) => {
        // notes:
        //  - do not call backoff here, it will throw an error if in-progress, the close event also fires on error.
        //  - the ws error object carries almost no info (intentionally by the spec to protect something-or-other serverside)
        triggerEventListeners('error', evt);
    };
    // todo: as teardown/stop
    function _cleanup() {
        // the webSocket object will be replaced by a new one and garbage collected but cleaning up just in case it emits anything more
        if (!webSocket) {
            return;
        }
        webSocket.removeEventListener('open', onOpen);
        webSocket.removeEventListener('close', onClose);
        webSocket.removeEventListener('error', onError);
        webSocket.removeEventListener('message', onMessage);
        webSocket.removeEventListener('ping', _markActivity);
        webSocket.removeEventListener('pong', _markActivity);
        webSocket = undefined;
    }
    const _promisedConnectionAttempt = () => __awaiter(this, void 0, void 0, function* () {
        if (!_connectionDesired) {
            // logger.debug('Explicitly disconnected, will not attempt to reconnect.');
            return;
        }
        if (_hasEverConnected) {
            _connectionDesired = yield isReconnectDesiredFn();
        }
        const url = yield fetchUrlFn();
        _sanitizedUrlForLog = _sanitizeAuthUrl(url);
        webSocket = webSocketFactory(url, opts.newWebSocketOpts);
        if (webSocket) {
            webSocket.addEventListener('open', onOpen);
            webSocket.addEventListener('close', onClose);
            webSocket.addEventListener('error', onError);
            webSocket.addEventListener('message', onMessage);
            webSocket.addEventListener('ping', _markActivity);
            webSocket.addEventListener('pong', _markActivity);
        }
        onConnectionAttempt && onConnectionAttempt(webSocket);
    });
    const attemptConnection = () => {
        _promisedConnectionAttempt()
            .catch((err) => {
            // console.error('Connection error', err);
            logger.error({ error: shared_utils_iso_1.jsErrorToRecord(err) }, 'Websocket connection attempt failed');
        });
    };
    fibonacciBackoff.on('ready', function (number, delay) {
        attemptConnection();
    });
    attemptConnection();
    function disconnect() {
        _connectionDesired = false;
        try {
            webSocket && webSocket.close && webSocket.close();
        }
        catch (err) {
            logger.error({ error: shared_utils_iso_1.jsErrorToRecord(err) }, 'Error trying to close websocket');
        }
    }
    const reconnect = () => {
        _connectionDesired = true;
        attemptConnection();
    };
    return {
        addWebSocketListener,
        disconnect,
        reconnect,
        getCurrentWebSocket,
        removeWebSocketListener,
        isAutoReconnectEnabled: () => _connectionDesired,
    };
}
exports._createWebsocketConnection = _createWebsocketConnection;
/**
 * Returns url without query search params which are frequently used to hold auth tokens.
 * (Though very short lived and prob ok to log.)
 *
 */
function _sanitizeAuthUrl(val) {
    const parts = val.split('?');
    return parts[0];
}
//# sourceMappingURL=reconnecting-websocket.js.map